import React, { useRef, useEffect } from 'react';

import { Grid, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useInView } from 'react-intersection-observer';

import { gsap, Power3 } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import lockup from '../assets/Lockup.svg';
import lockupM from '../assets/LockupM.svg';

const video =
  'https://gilead-blind-angels.s3.amazonaws.com/loopVideos/compressed/2021-10-04-gilead-hub-loop-video-desktop-1_vsjHVo67.mp4';
const videoM =
  'https://gilead-blind-angels.s3.amazonaws.com/loopVideos/compressed/2021-10-05-gilead-hub-loop-video-mobile-1_PVGhVIjL.mp4';

gsap.registerPlugin(ScrollTrigger);

const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: '100%',
    height: '100vh',
    marginTop: '-4.5rem',
    [theme.breakpoints.down('sm')]: {
      height: '100vh',
      width: 'auto'
    }
  },
  background: {
    // width: '100%',
    height: '100vh',
    position: 'absolute',
    zIndex: -1,
    opacity: 0,
    top: 0,
    right: 0,
    left: 0,
    bottom: 0,
    [theme.breakpoints.down('sm')]: {
      height: '100vh'
    }
  },
  textCont: {
    marginTop: '60vh',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      width: '80%'
    }
  },
  lockup: {
    // marginTop: 'rem',
    marginTop: '-5rem',
    [theme.breakpoints.down('sm')]: {
      width: '95%'
    }
  },
  bodyParagraphs: {
    marginTop: '30rem',
    width: '47%',
    opacity: 0,
    textAlign: 'center',
    maxWidth: '648px',
    [theme.breakpoints.down('sm')]: {
      width: '80%',
      marginTop: '18rem'
    }
  },
  skipButtonCont: {
    position: 'fixed',
    bottom: '10%',
    right: '10%',

    [theme.breakpoints.down('md')]: {
      right: '55px',
      top: '150px'
    },
    [theme.breakpoints.down('xs')]: {
      top: '150px',
      right: '7%'
    }
  },
  skipButton: {
    border: '.5px solid #fff',
    backgroundColor: 'rgba(0,0,0,.5)',
    color: '#979797',
    fontFamily: 'neuePlakB',
    textAlign: 'center',
    padding: '.5rem 1rem .6rem 1rem',
    fontSize: '.875rem',
    lineHeight: '.8rem',
    letterSpacing: '3px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '.5rem',
      padding: '.2rem .5rem .3rem .5rem'
    }
  },
  // --------------------------------------------------------------------
  h2Mobile: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '18px',
      lineHeight: '32px',
      textAlign: 'center'
    }
  },
  body3M: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '18px',
      lineHeight: '32px',
      textAlign: 'center'
    }
  }
}));

const Hero = () => {
  const classes = useStyles();
  let bgRef = useRef(null);
  let lockupRef = useRef(null);
  let text1Ref = useRef(null);
  let text2Ref = useRef(null);
  let text3Ref = useRef(null);
  let text4Ref = useRef(null);
  let text5Ref = useRef(null);
  let text6Ref = useRef(null);
  let text7Ref = useRef(null);
  let text8Ref = useRef(null);
  let text9Ref = useRef(null);

  const theme = useTheme();
  const matchesSM = useMediaQuery(theme.breakpoints.down('sm'));
  const { ref, inView } = useInView({ threshold: 0.05 });

  useEffect(() => {
    gsap.to(bgRef, { opacity: 1, duration: 8, delay: 1 });
    gsap.timeline({
      scrollTrigger: {
        trigger: bgRef,
        pin: true,
        pinSpacing: true,
        start: 'top top',
        end: '+=900%'
      }
    });
    // -----------------------
    gsap.to(lockupRef, {
      opacity: 0,
      ease: Power3,
      scrollTrigger: {
        trigger: lockupRef,
        start: 'bottom 50%',
        end: 'bottom top',
        pin: true,
        scrub: true
      }
    });
    // -----------------------

    gsap.fromTo(
      text1Ref,
      { opacity: 1 },
      {
        opacity: 0,
        scrollTrigger: {
          trigger: text1Ref,
          start: 'center 50%',
          end: 'top top',
          pin: true,
          scrub: true
        }
      }
    );
    // -----------------------

    gsap.fromTo(
      text2Ref,
      { opacity: 0 },
      {
        opacity: 1,
        scrollTrigger: {
          trigger: text2Ref,
          start: 'center bottom',
          end: 'top 50%',
          pin: true,
          scrub: true
        }
      }
    );
    gsap.fromTo(
      text2Ref,
      { opacity: 1 },
      {
        opacity: 0,
        scrollTrigger: {
          trigger: text2Ref,
          start: 'center 50%',
          end: 'top top',
          pin: true,
          scrub: true
        }
      }
    );

    // -----------------------

    gsap.fromTo(
      text3Ref,
      { opacity: 1 },
      {
        opacity: 0,
        scrollTrigger: {
          trigger: text3Ref,
          start: 'center 40%',
          end: 'top top',
          pin: true,
          scrub: true
        }
      }
    );

    // -----------------------
    gsap.fromTo(
      text4Ref,
      { opacity: 1 },
      {
        opacity: 0,
        scrollTrigger: {
          trigger: text4Ref,
          start: 'center 40%',
          end: 'top top',
          pin: true,
          scrub: true
        }
      }
    );
    // -----------------------
    gsap.fromTo(
      text5Ref,
      { opacity: 1 },
      {
        opacity: 0,
        scrollTrigger: {
          trigger: text5Ref,
          start: 'center 40%',
          end: 'top top',
          pin: true,
          scrub: true
        }
      }
    );
    // -----------------------

    gsap.fromTo(
      text6Ref,
      { opacity: 1 },
      {
        opacity: 0,
        scrollTrigger: {
          trigger: text6Ref,
          start: 'center 40%',
          end: 'top top',
          pin: true,
          scrub: true
        }
      }
    );

    // -----------------------

    gsap.fromTo(
      text7Ref,
      { opacity: 1 },
      {
        opacity: 0,
        scrollTrigger: {
          trigger: text7Ref,
          start: 'center 40%',
          end: 'top top',
          pin: true,
          scrub: true
        }
      }
    );

    // -----------------------

    gsap.fromTo(
      text8Ref,
      { opacity: 1 },
      {
        opacity: 0,
        scrollTrigger: {
          trigger: text8Ref,
          start: 'center 40%',
          end: 'top top',
          pin: true,
          scrub: true
        }
      }
    );
    gsap.fromTo(
      text9Ref,
      { opacity: 1 },
      {
        opacity: 0,
        scrollTrigger: {
          trigger: text9Ref,
          start: 'center 40%',
          end: 'top top',
          pin: true,
          scrub: true
        }
      }
    );
  }, [
    bgRef,
    text1Ref,
    text2Ref,
    text3Ref,
    text5Ref,
    text4Ref,
    text6Ref,
    text7Ref,
    text8Ref,
    text9Ref,
    lockupRef
  ]);

  return (
    <Grid
      id="home"
      container
      justifyContent="center"
      alignItems="center"
      className={classes.wrapper}
    >
      <Grid
        ref={(el) => {
          bgRef = el;
        }}
        id="bg"
        item
        className={classes.background}
      >
        {' '}
        {matchesSM ? (
          <video autoPlay muted loop playsInline src={videoM} style={{ width: '100%' }} />
        ) : (
          <video autoPlay muted loop src={video} />
        )}
      </Grid>
      {inView ? (
        <Grid className={classes.skipButtonCont}>
          <a href="#trailer">
            <button className={classes.skipButton}>SKIP INTRO</button>
          </a>
        </Grid>
      ) : null}

      <Grid ref={ref} container direction="column" alignItems="center" className={classes.textCont}>
        <Grid
          ref={(el) => {
            lockupRef = el;
          }}
          item
          className={classes.lockup}
        >
          <img src={matchesSM ? lockupM : lockup} alt="green text" style={{ width: '100%' }} />
        </Grid>
        <Grid
          ref={(el) => {
            text1Ref = el;
          }}
          item
          className={classes.bodyParagraphs}
          style={{ marginTop: '45vh' }}
        >
          <Typography variant="h2" className={classes.h2Mobile}>
            THE HIV EPIDEMIC HAS NOT ENDED
          </Typography>
        </Grid>
        <Grid
          ref={(el) => {
            text2Ref = el;
          }}
          item
          className={classes.bodyParagraphs}
        >
          <Typography variant="body1" className={classes.body3M} style={{ textAlign: 'center' }}>
            The disease that shook a generation is now preventable and treatable with proper medical
            care.
          </Typography>
        </Grid>
        <Grid
          ref={(el) => {
            text3Ref = el;
          }}
          item
          className={classes.bodyParagraphs}
        >
          <Typography variant="body1" className={classes.body3M} style={{ textAlign: 'center' }}>
            There is no cure for HIV, but thanks to scientific advancements, people can talk to a
            healthcare provider about options to help protect themselves and their partners.
          </Typography>
        </Grid>
        <Grid
          ref={(el) => {
            text9Ref = el;
          }}
          item
          className={classes.bodyParagraphs}
        >
          <Typography variant="body1" className={classes.body3M} style={{ textAlign: 'center' }}>
            While we now have tools to help stop HIV in its tracks, the disease is far from
            eradicated. It continues to affect thousands of Americans, and its ongoing impact
            reflects the deep inequalities that divide us.
          </Typography>
        </Grid>
        <Grid
          ref={(el) => {
            text4Ref = el;
          }}
          item
          className={classes.bodyParagraphs}
        >
          <Typography variant="body1" className={classes.body3M} style={{ textAlign: 'center' }}>
            In 2018, there were more than <span style={{ color: '#BFDA1C' }}>1 million </span>{' '}
            Americans living with HIV.
          </Typography>
        </Grid>
        <Grid
          ref={(el) => {
            text5Ref = el;
          }}
          item
          className={classes.bodyParagraphs}
        >
          <Typography variant="body1" className={classes.body3M} style={{ textAlign: 'center' }}>
            Nearly half of new HIV infections were in the South.
          </Typography>
        </Grid>
        <Grid
          ref={(el) => {
            text6Ref = el;
          }}
          item
          className={classes.bodyParagraphs}
        >
          <Typography variant="body1" className={classes.body3M} style={{ textAlign: 'center' }}>
            But across the South, there are leaders working within their communities to fight for
            the access, education, and resources that will help end the epidemic.
          </Typography>
        </Grid>
        <Grid
          ref={(el) => {
            text7Ref = el;
          }}
          item
          className={classes.bodyParagraphs}
        >
          <Typography variant="body1" className={classes.body3M} style={{ textAlign: 'center' }}>
            <span style={{ color: '#BFDA1C', fontFamily: 'neuePlakB' }}> BLIND ANGELS </span>is the
            story of those people.
            <br /> And it isn’t just a story of survival.
          </Typography>
        </Grid>
        <Grid
          ref={(el) => {
            text8Ref = el;
          }}
          item
          className={classes.bodyParagraphs}
        >
          <Typography variant="body1" className={classes.body3M} style={{ textAlign: 'center' }}>
            It’s a story of courage, family, and love.
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Hero;
